import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Download, CheckCircle, XCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';

export function DashboardExport({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [exportStatus, setExportStatus] = useState(null); // null | 'preparing' | 'ready' | 'error'
  const [exportProgress, setExportProgress] = useState(0);
  const [exportType, setExportType] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileName, setFileName] = useState('');

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cleanFilename = (str) => {
    return str.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  };

  const updateProgress = (current, total) => {
    const progress = Math.round((current / total) * 100);
    setExportProgress(progress);
  };

  const collectAllCSVData = () => {
    const sections = Array.from(document.querySelectorAll('.exportable-section'));
    let allCSVContent = "";
    
    sections.forEach(section => {
      const exportData = section.dataset.exportData ? JSON.parse(section.dataset.exportData) : null;
      if (!exportData) return;
      
      allCSVContent += `=== ${exportData.title} ===\n`;
      allCSVContent += exportData.headers.join(',') + '\n';
      exportData.rows.forEach(row => {
        allCSVContent += row.join(',') + '\n';
      });
      allCSVContent += '\n';
    });

    return allCSVContent;
  };

  const exportAllToCSV = async () => {
    setExportType('csv');
    setExportStatus('preparing');
    setFileName(`waste-dashboard-${new Date().toISOString().slice(0,10)}.csv`);
    updateProgress(0, 1);

    try {
      const csvContent = collectAllCSVData();
      if (!csvContent) {
        setExportStatus('error');
        return;
      }

      // Simulate processing delay for consistent UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
      setExportStatus('ready');
      updateProgress(1, 1);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExportStatus('error');
    }
  };

const exportToZIP = async () => {
  setExportType('zip');
  setExportStatus('preparing');
  setFileName(`waste-dashboard-${new Date().toISOString().slice(0,10)}.zip`);

  try {
    const zip = new JSZip();
    // Filter out sections with chart_5 class
    const sections = Array.from(document.querySelectorAll('.exportable-section'))
      .filter(section => !section.classList.contains('chart_5'));
    
    let completed = 0;
    const batchSize = 3;

    for (let i = 0; i < sections.length; i += batchSize) {
      const batch = sections.slice(i, i + batchSize);

      await Promise.all(batch.map(async (section, index) => {
        const title = section.querySelector('h4')?.textContent || `chart-${i + index}`;

        // Hide export controls temporarily
        const controls = section.querySelector('.export-controls, .waste-composition-export, .waste-generation-export, .peak-usage-export, .fill-level-export, .peak-fill-level-export, .heatmap-export-controls, .help-circle, .bin-usage-export-controls');
        const prevDisplay = controls?.style.display;
        if (controls) controls.style.display = 'none';

        try {
          const canvas = await html2canvas(section, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#FFFFFF',
            allowTaint: true
          });

          await new Promise((resolve) => {
            canvas.toBlob(blob => {
              if (blob) {
                zip.file(`${cleanFilename(title)}.png`, blob);
              }
              completed++;
              updateProgress(completed, sections.length);
              resolve();
            }, 'image/png', 0.8);
          });
        } catch (error) {
          console.error(`Error exporting ${title}:`, error);
          completed++;
          updateProgress(completed, sections.length);
        } finally {
          // Restore visibility after screenshot
          if (controls) controls.style.display = prevDisplay || '';
        }
      }));
    }

    const content = await zip.generateAsync({ type: 'blob' }, (metadata) => {
      updateProgress(95 + (metadata.percent / 20), 100);
    });

    const url = URL.createObjectURL(content);
    setDownloadUrl(url);
    setExportStatus('ready');
  } catch (error) {
    console.error('Error creating ZIP:', error);
    setExportStatus('error');
  }
};

  const exportToPDF = async () => {
    setExportType('pdf');
    setExportStatus('preparing');
    setFileName(`waste-dashboard-${new Date().toISOString().slice(0,10)}.pdf`);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const sections = Array.from(document.querySelectorAll('.exportable-section'));
      let completed = 0;
      
      // Process sections sequentially for PDF (better page control)
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const title = section.querySelector('h4')?.textContent || `Waste Chart ${i+1}`;
        
        try {
          const canvas = await html2canvas(section, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#FFFFFF',
            allowTaint: true
          });

          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 180;
          const pageHeight = 277;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          if (i > 0) {
            pdf.addPage();
          }

          pdf.setFontSize(12);
          pdf.text(title, 15, 15);
          pdf.addImage(imgData, 'PNG', 15, 25, imgWidth, imgHeight);
          
          completed++;
          updateProgress(completed, sections.length);
        } catch (error) {
          console.error(`Error exporting ${title} to PDF:`, error);
          completed++;
          updateProgress(completed, sections.length);
          continue;
        }
      }

      // Generate PDF blob
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setDownloadUrl(url);
      setExportStatus('ready');
    } catch (error) {
      console.error('Error creating PDF:', error);
      setExportStatus('error');
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      saveAs(downloadUrl, fileName);
      // Clean up
      URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
      setExportStatus(null);
    }
  };

  const cancelExport = () => {
    setExportStatus(null);
    setDownloadUrl(null);
  };

  return (
    <div className="dashboard-export-container relative">
      {/* Export status notification (non-modal) */}
      {exportStatus && (
        <div className="fixed bottom-4 left-4 z-50 bg-background p-4 rounded-lg shadow-lg border flex items-center gap-4">
          {exportStatus === 'preparing' && (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <div>
                <p className="font-medium">Preparing {exportType?.toUpperCase()} export</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${exportProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {exportProgress}% complete - You can continue using the dashboard
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={cancelExport}>
                Cancel
              </Button>
            </>
          )}
          
          {exportStatus === 'ready' && (
            <>
              <CheckCircle className="h-6 w-6 text-green-500" />
              <div>
                <p className="font-medium">Export ready</p>
                <p className="text-xs text-muted-foreground">
                  {fileName}
                </p>
              </div>
              <Button onClick={handleDownload}>
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelExport}>
                Dismiss
              </Button>
            </>
          )}
          
          {exportStatus === 'error' && (
            <>
              <XCircle className="h-6 w-6 text-red-500" />
              <div>
                <p className="font-medium">Export failed</p>
                <p className="text-xs text-muted-foreground">
                  Try again or select a different format
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={cancelExport}>
                Dismiss
              </Button>
            </>
          )}
        </div>
      )}

      {/* Export button */}
      <div className="fixed bottom-4 right-4 z-40">
        <div className="relative">
          <Button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2"
            disabled={exportStatus === 'preparing'}
          >
            <Download className="w-4 h-4" />
            Export Data
          </Button>
          
          {isOpen && (
            <div 
              ref={dropdownRef}
              className="absolute bottom-full right-0 mb-2 w-48 bg-background rounded-md shadow-lg border p-1 z-50"
            >
              <Button 
                variant="ghost"
                className="w-full justify-start"
                onClick={exportAllToCSV}
                disabled={exportStatus === 'preparing'}
              >
                Export as CSV
              </Button>
              <Button 
                variant="ghost"
                className="w-full justify-start"
                onClick={exportToZIP}
                disabled={exportStatus === 'preparing'}
              >
                Export as PNG (ZIP)
              </Button>

            </div>
          )}
        </div>
      </div>
      
      {children}
    </div>
  );
}